/**
 * reportUi — shared presentation primitives cho Production Report tabs.
 *
 * Trích từ BomTab (style đã chốt ở `/baoloc/report/batch-report/bom`) để các tab
 * khác (Production Analysis…) dùng CHUNG, không drift:
 *   - Number format kế toán: số âm → ngoặc tròn đỏ, `tabular-nums`, font 0.7rem.
 *   - BlockTitle: uppercase + accent bar primary.
 *   - ExLabel / ExVal / ExField: nhãn 0.66rem uppercase muted + value 0.7rem bold.
 *   - QualityChip: chip code + % primary.
 */
import { Box, Chip, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'

import { fieldLabelSx, fmtN, fmtPct } from './reportFormat'
import { COLORS } from './styleTokens'

/** Giá trị reference (LIFFE/Diff) read-only, prominent (màu primary, đậm) —
 *  load từ v_changecost_quality nên không cho sửa tay. `tag` = Ro/Ara. */
export function RefNum({ value, tag }: { value: number | null | undefined; tag?: string }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.4 }}>
      {tag ? (
        <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 700, color: COLORS.textSubtle }}>
          {tag}
        </Typography>
      ) : null}
      <Typography
        component="span"
        sx={{ fontSize: '0.7rem', fontWeight: 900, color: COLORS.primary, fontVariantNumeric: 'tabular-nums' }}
      >
        {fmtN(value)}
      </Typography>
    </Box>
  )
}

/** % cell kế toán: `frac` là phân số (0..1) → ×100 + '%'; số âm → đỏ; tabular-nums. */
export function PctText({ frac, d = 2 }: { frac: number | null | undefined; d?: number }) {
  if (frac == null || !Number.isFinite(Number(frac))) return <>–</>
  const n = Number(frac) * 100
  const neg = n < 0
  return (
    <Box
      component="span"
      sx={{
        fontSize: '0.7rem',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        ...(neg ? { color: COLORS.danger, fontWeight: 600 } : {}),
      }}
    >
      {fmtPct(n, d)}
    </Box>
  )
}

/** Số có format: âm → ngoặc tròn + màu đỏ (§4). `usd` → prefix `$`.
 *  Căn cột (Excel accounting): số dương reserve đúng bề rộng `(` `)` (render trong
 *  suốt) để mép `$`/chữ số thẳng hàng với số âm; `tabular-nums` cho digit đều width. */
export function NumText({ n, d = 2, usd }: { n: number | null | undefined; d?: number; usd?: boolean }) {
  if (n == null || !Number.isFinite(n)) return <>–</>
  const neg = n < 0
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
  const body = usd ? `$${abs}` : abs
  return (
    <Box
      component="span"
      sx={{
        fontSize: '0.7rem',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        ...(neg ? { color: COLORS.danger, fontWeight: 600 } : {}),
      }}
    >
      <Box component="span" sx={{ opacity: neg ? 1 : 0 }}>(</Box>
      {body}
      <Box component="span" sx={{ opacity: neg ? 1 : 0 }}>)</Box>
    </Box>
  )
}

/* ---------- label / value primitives ------------------------------------ */

/** Tiêu đề block — uppercase + accent bar trái; giúp gọi tên từng vùng report. */
export function BlockTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="div"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        mb: 0.75,
        pl: 0.25,
        fontSize: '0.7rem',
        fontWeight: 800,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: COLORS.primary,
      }}
    >
      <Box component="span" sx={{ width: 3, height: 13, borderRadius: 1, bgcolor: COLORS.primary }} />
      {children}
    </Typography>
  )
}

export function ExLabel({ children }: { children: ReactNode }) {
  return <Typography sx={fieldLabelSx}>{children}</Typography>
}

export function ExVal({ children }: { children: ReactNode }) {
  // Value số → căn phải (Access convention).
  return <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: COLORS.text, textAlign: 'right' }}>{children}</Typography>
}

/** Label + value inline (mirror 1 ô Excel label/value). */
export function ExField({
  label,
  value,
  tone = 'neutral',
  strong,
  wrap,
  alignLeft,
}: {
  label: string
  value: ReactNode
  tone?: 'neutral' | 'warn' | 'danger' | 'success'
  strong?: boolean
  wrap?: boolean
  alignLeft?: boolean
}) {
  const color = tone === 'warn' ? COLORS.warn : tone === 'danger' ? COLORS.danger : tone === 'success' ? COLORS.success : COLORS.text
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="baseline"
      justifyContent={alignLeft ? 'flex-start' : 'space-between'}
      sx={{ minWidth: 0, width: '100%' }}
    >
      <Typography sx={{ ...fieldLabelSx, flexShrink: 0 }}>{label}</Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: strong ? 900 : 800,
            color,
            textAlign: alignLeft ? 'left' : 'right',
            whiteSpace: wrap ? 'normal' : 'nowrap',
            wordBreak: wrap ? 'break-word' : 'normal',
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
    </Stack>
  )
}

/** Chip cho 1 quality code — kèm % (In/Out) phía sau (màu primary). */
export function QualityChip({ label, pct }: { label: string; pct?: string }) {
  return (
    <Chip
      size="small"
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
          <Box component="span">{label}</Box>
          {pct ? <Box component="span" sx={{ color: COLORS.primary, fontWeight: 800 }}>{pct}</Box> : null}
        </Box>
      }
      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: COLORS.bgSubtle, color: COLORS.text, border: `1px solid ${COLORS.border}`, '& .MuiChip-label': { px: 0.75 } }}
    />
  )
}
